import { useToast } from '@paygine/mtd-uikit'
import { useIntervalFn } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { ref } from 'vue'

import type { sbpBanksModel } from '@/entities/sbp-banks'

import { userDataModel } from '@/entities/user-data'
import { isErrorResponse } from '@/shared/api'
import { useConfig } from '@/shared/config'
import { normalizeNumber } from '@/shared/lib/formatting'

import {
    changeStatusSubscription,
    createSbpSubscription,
    deletePayMethod,
    fetchStatusSubcription,
    getPayMethods,
    type SubscriptionStatus,
} from '../api'

export function useSubscription() {
    const toaster = useToast()
    const userDataStore = userDataModel.useModel()
    const isLoading = ref(false)
    const isFetchingPayMethods = ref(false)
    const { config } = useConfig()
    const { userData } = storeToRefs(userDataStore)
    const payMethods = ref<null | sbpBanksModel.SbpBank[]>(null)

    const subscriptionStatus = ref<null | SubscriptionStatus>(null)
    const attemptsCount = ref(0)

    async function updateStatusSubscription(methodId: number, status: boolean) {
        isLoading.value = true
        const { data, response } = await changeStatusSubscription(methodId, status)
        isLoading.value = false

        if (!response?.ok || (data && isErrorResponse(data))) return false
        return true
    }

    async function addSbpSubscription() {
        isLoading.value = true
        const { data, response } = await createSbpSubscription()
        isLoading.value = false

        if (!response?.ok || (data && isErrorResponse(data))) {
            return null
        }
        const redirectUrl = data?.redirect_url || null
        return redirectUrl
    }

    const { pause: stopCheckingStatus, resume: startCheckingStatus } = useIntervalFn(
        async () => {
            attemptsCount.value += 1

            if (attemptsCount.value > 5) {
                if (subscriptionStatus.value === 'PENDING' || !subscriptionStatus.value) {
                    subscriptionStatus.value = 'ERROR'
                }
                stopCheckingStatus()
                return
            }

            await checkStatusSubscription()
        },
        3000,
        {
            immediate: false,
        }
    )

    async function checkStatusSubscription() {
        const response = await fetchStatusSubcription(
            config.sector,
            normalizeNumber(userData.value?.phone || '')
        )

        if (!response || !('status' in response)) {
            subscriptionStatus.value = 'ERROR'
        }

        subscriptionStatus.value = response.status
    }

    async function fetchPayMethods() {
        isFetchingPayMethods.value = true

        const response = await getPayMethods()

        isFetchingPayMethods.value = false

        if (!response || !response?.sbp_banks_list?.length) {
            throw new Error('список банков не был получен!')
        }

        payMethods.value = response.sbp_banks_list
            .filter((bank) => Boolean(bank.sbp_url))
            .sort((a, b) => {
                const orderA = Number(a.order)
                const orderB = Number(b.order)
                // Сортируем по порядку (по возрастанию)
                return orderA - orderB
            })
    }

    async function removePayMethod(methodId: number) {
        isLoading.value = true

        const { response } = await deletePayMethod(methodId, config.sector)

        isLoading.value = false

        if (response?.ok) {
            await userDataStore.fetchUserData()
            return true
        }

        toaster.add({
            message: 'Повторите попытку позже',
            title: 'Не удалось удалить способ оплаты',
            type: 'DANGER',
        })

        return false
    }

    return {
        addSbpSubscription,
        checkStatusSubscription,
        fetchPayMethods,
        isFetchingPayMethods,
        isLoading,
        payMethods,
        removePayMethod,
        startCheckingStatus,
        stopCheckingStatus,
        subscriptionStatus,
        updateStatusSubscription,
    }
}
